import gql from 'graphql-tag';
import { DateTime } from 'luxon';
import { useMemo, useRef } from 'react';
import { BannerFragment } from '@graphqlTypes';
import { Container, HeroBorder, RevealWrapper, RhLink } from '@components';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { getLinkData } from '@/core/utils';
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import ArrowTailRight from '@/assets/svg/arrow-tail-right.svg';

import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import className from 'classnames/bind';
import styles from './Banner.module.scss';
const cx = className.bind(styles);

type BannerProps = Omit<BannerFragment, '__typename'>;

export default function Banner(props: BannerProps) {
  const { type, largeBorder, eyebrow, headline, subhead, link } = props;
  const { labels, todaysEvents } = usePageWrapperData();
  const [bannerType] = type;
  const isEvent = bannerType === 'event';
  const [event] = todaysEvents;

  const isEventToday = useMemo(() => {
    if (!event?.eventData?.startDate) return false;
    const now = DateTime.now().setZone('America/New_York');
    const eventStart = DateTime.fromISO(event.eventData.startDate, { zone: 'America/New_York' });
    const eventEnd = event.eventData.endDate
      ? DateTime.fromISO(event.eventData.endDate, { zone: 'America/New_York' })
      : eventStart;
    const todayStart = now.startOf('day');
    const todayEnd = now.endOf('day');
    return (eventStart <= todayEnd && eventEnd >= todayStart);
  }, [event]);

  const bannerEyebrow = isEvent ? labels?.eventTodayLabel : eyebrow;
  const title = isEvent ? event?.title : headline;
  const body = isEvent ? event?.eventData?.eventDescription : subhead;
  const linkData = getLinkData(link, true);
  const linkUrl = isEvent ? event?.uri : linkData?.url;
  const linkTarget = isEvent ? '_self' : linkData?.target;

  // -------------------- Animation

  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const renderEvent = useMemo(() => {
    return isEvent && event && isEventToday && mounted;
  }, [mounted]);
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = (mounted && isReady && isDesktop) ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  // const isRevealEnabled = true;
  const animateInnerElements = false; // Note: Creative feedback was to remove inner element animations -Gabe

  const createMotionProps = (delay: number = 0) => ({
    initial: animateInnerElements ? {
      opacity: REVEAL.opacity,
      y: REVEAL.sm.distance
    } : {},
    animate: animateInnerElements && isInView ? {
      opacity: 1,
      y: 0
    } : {},
    transition: {
      duration: REVEAL.sm.duration,
      ease: REVEAL.ease,
      delay
    }
  });

  // Define staggered delays
  const revealDelayEyebrow = REVEAL.lg.delay;
  const revealDelayTitle = revealDelayEyebrow + REVEAL.sm.delay;
  const revealDelayBody = revealDelayTitle + REVEAL.sm.delay;

  // -------------------- Render

  if (!renderEvent && isEvent) return null;

  return (
    <section className={cx("banner-wrapper")} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx("banner")}>
          <RhLink href={linkUrl || ""} target={linkTarget} data-gtm-section="banner" className={cx("link", { 'large-border': largeBorder })}>
            { largeBorder && <HeroBorder small={true} className={cx("border")} />}
            <div className={cx("content")}>

              { bannerEyebrow && (
                <motion.p
                  className={cx("eyebrow-l", "eyebrow")}
                  {...createMotionProps(revealDelayEyebrow)}
                >
                  {bannerEyebrow}
                </motion.p>
              )}

              <motion.p
                className={cx("title-lc-s", "title")}
                {...createMotionProps(revealDelayTitle)}
              >
                {title}
              </motion.p>

              { body &&
                <motion.p
                  className={cx("text-m", "body")}
                  {...createMotionProps(revealDelayBody)}
                >
                  {body}
                </motion.p>
              }

            </div>
            <ArrowTailRight className={cx("arrow")} />
          </RhLink>
        </Container>
      </RevealWrapper>
    </section>
  );

}

Banner.fragments = {
  entry: gql`
    fragment Banner on FlexiblePageContentContentBannerLayout {
      type
      largeBorder
      eyebrow
      headline
      subhead
      link {
        linkType
        externalLink
        sectionId
        page {
          node {
            uri
          }
        }
      }
    }
  `,
};
